import React from 'react';
import { Package } from 'lucide-react';
import LoanPageTemplate from '../../components/LoanPageTemplate';

const AssetFinance = () => (
  <LoanPageTemplate
    icon={Package}
    path="/asset-finance"
    title="Asset Finance"
    subtitle="Lease or purchase business equipment, vehicles, and machinery without tying up working capital. Flexible terms from specialist lenders."
    description="Asset finance lets Australian businesses acquire vehicles, machinery, technology, and equipment by spreading the cost over the useful life of the asset. Instead of paying cash upfront and depleting your reserves, you preserve working capital while still claiming tax deductions on depreciation and interest. We work with you to structure asset finance facilities around your cash flow and the asset's productive life, sourcing options from a panel of specialist lenders with competitive rates."
    features={[
      {
        title: 'Chattel Mortgage',
        description: 'You own the asset from day one while the lender holds a mortgage over it. This structure suits GST-registered businesses because you claim the GST credit upfront and depreciate the asset on your balance sheet.'
      },
      {
        title: 'Finance Lease',
        description: 'The lender owns the asset and leases it to you for a fixed term. Lease payments are fully tax-deductible as an operating expense. At the end, you can purchase the asset for a pre-agreed residual value.'
      },
      {
        title: 'Operating Lease',
        description: 'Ideal for assets that depreciate quickly like technology and vehicles. You use the asset for a set period and return it at the end. Payments are off-balance-sheet, which can improve your financial ratios.'
      },
      {
        title: 'Hire Purchase',
        description: 'Similar to a chattel mortgage but the lender retains ownership until the final payment. You can claim depreciation and interest deductions. Popular with sole traders and partnerships.'
      },
      {
        title: 'Equipment Refinance & Sale-Leaseback',
        description: 'Unlock cash tied up in equipment you already own. We arrange sale-leaseback facilities where a lender purchases your existing asset and leases it back to you, releasing capital for growth or debt reduction.'
      },
      {
        title: 'Instant Asset Write-Off',
        description: 'Eligible businesses can immediately deduct the cost of qualifying assets under the Australian Government\'s instant asset write-off provisions. We structure your finance to maximise this benefit and advise on current thresholds.'
      }
    ]}
    process={[
      {
        title: 'Asset & Structure Assessment',
        description: 'We discuss the asset you need, its expected useful life, and your business structure to recommend the most tax-effective finance option: chattel mortgage, lease, hire purchase, or operating lease.'
      },
      {
        title: 'Funding Comparison',
        description: 'We compare rates and terms from specialist asset finance lenders, banks, and manufacturer finance programs. For high-value assets, we negotiate bespoke terms based on your business strength.'
      },
      {
        title: 'Application & Approval',
        description: 'Asset finance applications are often approved within 24-48 hours for amounts under $150,000 using low-doc processes. Larger or more complex facilities may require full financials and take 5-10 business days.'
      },
      {
        title: 'Settlement & Asset Delivery',
        description: 'Once approved, the lender pays the supplier directly and the security interest is registered on the PPSR. You take delivery of the asset and begin making repayments as agreed.'
      }
    ]}
    faqs={[
      {
        q: 'What is the difference between a chattel mortgage and a finance lease?',
        a: 'With a chattel mortgage, you own the asset immediately and the lender holds a mortgage over it as security. You claim GST upfront and depreciate the asset. With a finance lease, the lender owns the asset and leases it to you. Lease payments are tax-deductible but you do not claim the GST upfront. The best option depends on your GST registration status, cash flow preference, and accountant\'s advice.'
      },
      {
        q: 'Can I finance used or second-hand equipment?',
        a: 'Yes. Most asset finance lenders will fund used equipment, though the age and condition of the asset may affect the available term and interest rate. Lenders typically want the asset to have a remaining useful life that exceeds the loan term. We work with funders who specialise in used equipment across construction, transport, manufacturing, and agriculture.'
      },
      {
        q: 'What is the instant asset write-off and does it apply to financed assets?',
        a: 'The instant asset write-off allows eligible Australian businesses to immediately deduct the full cost of qualifying assets up to a set threshold in the financial year they are first used or installed. This applies to assets purchased via chattel mortgage and hire purchase because you own the asset. Leased assets are treated differently for tax purposes. The threshold and eligibility change frequently, so we recommend confirming with your accountant.'
      },
      {
        q: 'Do I need a deposit for asset finance?',
        a: 'Most asset finance products allow 100% funding with no deposit required. However, contributing a deposit or trade-in reduces your loan amount and may improve your rate. For chattel mortgages, a 10-20% deposit can also reduce your regular repayments meaningfully. We advise on the optimal structure based on your cash position.'
      },
      {
        q: 'How long can I finance equipment for?',
        a: 'Asset finance terms typically range from 1 to 7 years, depending on the asset type and its expected useful life. Vehicles and light commercial equipment commonly finance over 3-5 years. Heavy machinery and specialised equipment may qualify for longer terms. We match the finance term to the asset\'s productive life to avoid paying for equipment after it has been retired.'
      }
    ]}
    loanType="Asset Finance"
    color="from-teal-600 to-teal-800"
  />
);

export default AssetFinance;
